import { DatePipe, NgClass, NgForOf, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { MatChipsModule } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect, MatSelectChange, MatSelectTrigger } from '@angular/material/select';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { HomePatientBasicInfoComponent } from '../../components/home-page/home-patient-basic-info/home-patient-basic-info.component';
import { HomePatientTasksComponent } from '../../components/home-page/home-patient-tasks/home-patient-tasks.component';
import { BreadcrumbComponent } from '../../components/utility/breadcrumb/breadcrumb.component';
import { UserToken } from '../../models/auth.model';
import { Settings } from '../../models/settings.model';
import {
  PatientListGroup,
  PatientListIdentifier,
  VcPatientListItem,
} from '../../models/view-content.models/view-content.model';
import { ViewContentCacheService } from '../../services/cache/view-content-cache.service';
import { AccessFacadeService } from '../../services/facades/access-facade.service';
import { SettingsFacadeService } from '../../services/facades/settings-facade.service';
import { ToolboxService } from '../../services/toolbox.service';

// #region Interfaces
interface FilterOptions {
  key: string;
  value: string;
  selected: boolean;
}

interface SortOptions {
  key: string;
  value: string;
  selected: boolean;
  order: 'DESC' | 'ASC';
}

interface ExtendedListIdentifier extends PatientListIdentifier {
  selected: boolean;
}

interface ExtendedPatientListGroup extends PatientListGroup {
  listIdentifier: ExtendedListIdentifier;
}

@Component({
  selector: 'app-organization-page',
  templateUrl: './organization-page.component.html',
  styleUrls: ['./organization-page.component.scss'],
  imports: [
    DatePipe,
    HomePatientBasicInfoComponent,
    HomePatientTasksComponent,
    MatCell,
    MatCellDef,
    MatChipsModule,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIcon,
    MatOption,
    MatRow,
    MatRowDef,
    MatSelect,
    MatTable,
    MatTooltip,
    NgClass,
    NgForOf,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    RouterLink,
    TranslateModule,
    MatInput,
    BreadcrumbComponent,
    MatSelectTrigger,
  ],
  standalone: true,
})
export class OrganizationPageComponent implements OnInit, OnDestroy {
  public patients: VcPatientListItem[] = [];
  public caseLists: ExtendedPatientListGroup[] = [];
  public allSubs: Subscription[] = [];
  public dataSource = new MatTableDataSource<VcPatientListItem>([]);
  public displayedColumns: string[] = [];
  public filterOptions: FilterOptions[] = [];
  public sortOptions: SortOptions[] = [];
  public toggleState: boolean = false;

  public translatedColumns: { [key: string]: string } = {};
  public activeFilter: { type: string; value: string } | undefined;

  private accessToken: UserToken | undefined;
  private viewedPatients: string[] = [];

  public constructor(
    private accessFacade: AccessFacadeService,
    private activatedRoute: ActivatedRoute,
    private toolbox: ToolboxService,
    private translate: TranslateService,
    private vcCache: ViewContentCacheService,
    private settingsFacade: SettingsFacadeService,
    private router: Router,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.allSubs.push(
      this.settingsFacade.settings$.subscribe(settings => {
        this.toggleState = settings.presentationMode;
        this.viewedPatients = settings.viewedPatients || [];
      }),
    );

    let initialDataLoaded = false;

    this.allSubs.push(
      this.accessFacade.userTokens$
        .pipe(
          filter(e => !!e.token),
          switchMap(async t => {
            await this.toolbox.executeInLoading(async () => {
              this.accessToken = t.token ?? undefined;
              if (this.accessToken) {
                await this.updateData();
                initialDataLoaded = true;
              }

              await this.initializeFiltersAndSort();
              await this.handleSelectedFilter(this.filterOptions[0]);
              await this.initializePatients();
            }, 'Loading Data...');
          }),
        )
        .subscribe(),
    );

    this.allSubs.push(
      this.activatedRoute.params.subscribe(params => {
        const stationName = params['stationName'];

        if (stationName && this.caseLists.length > 0) {
          const stationFromUrl = this.caseLists.find(
            station => station.listIdentifier.name === stationName,
          );

          if (stationFromUrl) {
            this.handleSelectedStation(stationFromUrl, false);
          } else {
            console.warn(`Station name "${stationName}" not found in case lists`);

            if (initialDataLoaded) {
              const caseInsensitiveMatch = this.caseLists.find(
                station => station.listIdentifier.name.toLowerCase() === stationName.toLowerCase(),
              );

              if (caseInsensitiveMatch) {
                this.handleSelectedStation(caseInsensitiveMatch, false);
              }
            }
          }
        }
      }),
    );

    this.allSubs.push(
      this.activatedRoute.queryParams.subscribe(() => {
        this.updateData().then(() => {
          const stationName = this.activatedRoute.snapshot.paramMap.get('stationName');
          if (stationName && this.caseLists.length > 0) {
            const stationFromUrl = this.caseLists.find(
              station => station.listIdentifier.name === stationName,
            );

            if (stationFromUrl) {
              this.handleSelectedStation(stationFromUrl, false);
            }
          }
        });
      }),
    );
  }
  public ngOnDestroy() {
    this.allSubs.forEach(sub => sub.unsubscribe());
  }

  public async handleSelectedStation(list: ExtendedPatientListGroup, updateUrl: boolean = true) {
    this.caseLists.forEach(l => {
      l.listIdentifier.selected = list.listIdentifier.name === l.listIdentifier.name;
    });

    if (updateUrl) {
      const isCurrentlyOnOrgRoute =
        this.router.url === '/organization' || this.router.url.startsWith('/organization/');

      if (isCurrentlyOnOrgRoute) {
        this.router.navigate(['/organization', list.listIdentifier.name], {
          replaceUrl: true,
          queryParams: this.activatedRoute.snapshot.queryParams,
        });
      } else {
        this.router.navigate(['/organization', list.listIdentifier.name], {
          queryParams: this.activatedRoute.snapshot.queryParams,
        });
      }
    }

    const selectedList = this.getSelectedList();
    if (selectedList) {
      const activeSort = this.sortOptions.find(s => s.selected);
      if (activeSort) {
        if (activeSort.key === 'name') {
          this.patients = this.sortByFullName(selectedList.patients, activeSort.order);
        } else if (activeSort.key === 'admissionDate') {
          this.patients = this.sortByAdmissionDate(selectedList.patients, activeSort.order);
        } else if (activeSort.key === 'ward') {
          this.patients = this.sortByWard(selectedList.patients, activeSort.order);
        }
      }
      this.refresh(selectedList.patients);
    } else {
      console.error('Keine Liste ausgewählt!');
    }
  }

  public async handleSelectedFilter(filter: FilterOptions) {
    const selectedList = this.getSelectedList();

    if (!selectedList) {
      console.error('Keine Liste ausgewählt!');
      return;
    }

    this.filterOptions.forEach(f => (f.selected = false));
    filter.selected = true;

    this.activeFilter = {
      type: 'filter_type',
      value: filter.value,
    };

    const translationKeys = this.getTranslationKeys(filter.key);
    const translations = await this.translate.get(translationKeys).toPromise();

    this.displayedColumns = translationKeys.map(key => translations[key]);
    this.translatedColumns = translations;

    this.updateCaseLists(selectedList);
    this.sortPatients(selectedList);
    this.refresh(selectedList.patients);
  }

  public searchValue(ev: EventTarget | null) {
    if (ev) {
      const searchTerm = (ev as HTMLInputElement).value.toLowerCase();
      const selectedList = this.getSelectedList();

      if (selectedList) {
        const patients = selectedList.patients.filter(patient => {
          if (patient) {
            return this.matchesPatient(patient, searchTerm);
          }
          return false;
        });
        this.refresh(patients);
      }
    }
  }

  public async onSelectSortOption(sort: SortOptions) {
    const selectedList = this.getSelectedList();
    if (!selectedList) {
      console.error('Keine Liste ausgewählt!');
      return;
    }

    this.sortOptions.forEach(s => (s.selected = false));
    sort.order = sort.order === 'ASC' ? 'DESC' : 'ASC';
    sort.selected = true;

    let patients: VcPatientListItem[] = [];

    if (sort.key === 'name') {
      patients = this.sortByFullName(selectedList.patients, sort.order);
    } else if (sort.key === 'admissionDate') {
      patients = this.sortByAdmissionDate(selectedList.patients, sort.order);
    } else if (sort.key === 'ward') {
      patients = this.sortByWard(selectedList.patients, sort.order);
    }

    this.refresh(patients);
  }

  public sortByAdmissionDate(
    patients: VcPatientListItem[],
    sortOrder: 'ASC' | 'DESC',
  ): VcPatientListItem[] {
    if (patients) {
      return patients.sort((a, b) => {
        const dateA = new Date(a.patient_details.case.data.admission_date).getTime();
        const dateB = new Date(b.patient_details.case.data.admission_date).getTime();
        return sortOrder === 'ASC' ? dateA - dateB : dateB - dateA;
      });
    }
    return [];
  }
  public sortByFullName(
    patients: VcPatientListItem[],
    sortOrder: 'ASC' | 'DESC',
  ): VcPatientListItem[] {
    if (patients) {
      return patients.sort((a, b) => {
        const surnameA = a.patient_details.patient.data.surname.toLowerCase();
        const surnameB = b.patient_details.patient.data.surname.toLowerCase();

        let compare = surnameA.localeCompare(surnameB);

        if (compare === 0) {
          const nameA = a.patient_details.patient.data.name.toLowerCase();
          const nameB = b.patient_details.patient.data.name.toLowerCase();
          compare = nameA.localeCompare(nameB);
        }

        return sortOrder === 'ASC' ? compare : -compare;
      });
    }
    return [];
  }

  private async initializeFiltersAndSort(): Promise<void> {
    const translations = await this.getTranslations([
      'COMPONENT.PAGE_WORKFLOW.filter_tasks',
      'COMPONENT.PAGE_WORKFLOW.filter_instruction',
      'COMPONENT.PAGE_WORKFLOW.filter_comments',
      'COMPONENT.PAGE_WORKFLOW.filter_findings',
      'COMPONENT.PAGE_WORKFLOW.admissionDate',
      'COMPONENT.PAGE_WORKFLOW.ward',
    ]);

    this.filterOptions = [
      {
        key: 'tasks',
        value: translations['COMPONENT.PAGE_WORKFLOW.filter_tasks'],
        selected: false,
      },
      {
        key: 'instruction',
        value: translations['COMPONENT.PAGE_WORKFLOW.filter_instruction'],
        selected: false,
      },
      {
        key: 'findings',
        value: translations['COMPONENT.PAGE_WORKFLOW.filter_findings'],
        selected: false,
      },
    ];

    this.sortOptions = [
      { key: 'name', value: 'Name', selected: true, order: 'ASC' },
      {
        key: 'admissionDate',
        value: translations['COMPONENT.PAGE_WORKFLOW.admissionDate'],
        selected: false,
        order: 'ASC',
      },
      {
        key: 'ward',
        value: translations['COMPONENT.PAGE_WORKFLOW.ward'],
        selected: false,
        order: 'ASC',
      },
    ];
  }
  private async initializePatients(): Promise<void> {
    const selectedList = this.getSelectedList();
    if (selectedList) {
      this.patients = this.sortByFullName(selectedList.patients, 'ASC');
      this.refresh(selectedList.patients);
    }
  }

  private getTranslations(keys: string[]): Promise<{ [key: string]: string }> {
    return firstValueFrom(this.translate.get(keys));
  }

  private getTranslationKeys(filterKey: string): string[] {
    const baseKey = 'COMPONENT.PAGE_WORKFLOW.column_';
    switch (filterKey) {
      case 'tasks':
        return ['general_information', 'task', 'goal_date', 'contractor', 'status'].map(
          k => baseKey + k,
        );
      case 'instruction':
        return [
          'general_information',
          'description',
          'submitted_to',
          'creation_date',
          'status',
        ].map(k => baseKey + k);
      case 'comments':
        return ['general_information', 'submitted_to', 'description'].map(k => baseKey + k);
      case 'findings':
        return [
          'general_information',
          'finding_category',
          'examination_date',
          'finding_date',
          'findings',
        ].map(k => baseKey + k);
      default:
        return [];
    }
  }

  private updateCaseLists(selectedList: any): void {
    this.caseLists.forEach(l => {
      l.listIdentifier.selected = l.listIdentifier.name === selectedList.listIdentifier.name;
    });
  }

  private sortPatients(selectedList: any): void {
    const activeSort = this.sortOptions.find(s => s.selected);
    if (activeSort) {
      if (activeSort.key === 'name') {
        this.patients = this.sortByFullName(selectedList.patients, activeSort.order);
      } else if (activeSort.key === 'admissionDate') {
        this.patients = this.sortByAdmissionDate(selectedList.patients, activeSort.order);
      } else if (activeSort.key === 'ward') {
        this.patients = this.sortByWard(selectedList.patients, activeSort.order);
      }
    }
  }

  private matchesPatient(patient: VcPatientListItem, query: string): boolean {
    const lowerCaseQuery = query.toLowerCase();

    const matchesSimpleFields = [
      patient.patient_details?.patient?.data?.name ?? '',
      patient.patient_details?.patient?.data?.surname ?? '',
      patient.patient_details?.current_place?.data?.room?.long_text ?? '',
      patient.patient_details?.case?.data?.admission_date ?? '',
    ].some(field => field.toLowerCase().includes(lowerCaseQuery));

    const matchesDiagnosis =
      patient.patient_details?.last_diagnosis?.data?.diagnose
        ?.toLowerCase()
        .includes(lowerCaseQuery) ?? false;

    const matchesTasks =
      patient.tasks?.some(
        (task: any) =>
          task.data?.task_name?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.details?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.priority?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.editor?.surname?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.editor?.name?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.goalDateOn?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.contractor?.surname?.toLowerCase().includes(lowerCaseQuery) ||
          task.data?.contractor?.name?.toLowerCase().includes(lowerCaseQuery),
      ) ?? false;

    const matchesVisits =
      patient.visit_record?.history?.some((visitRecord: any) =>
        visitRecord.data?.note?.toLowerCase().includes(lowerCaseQuery),
      ) ?? false;

    const matchesDischarge =
      patient.patient_details?.case?.data?.discharge_date?.toLowerCase().includes(lowerCaseQuery) ||
      patient.patient_details?.case?.data?.planned_discharge_date
        ?.toLowerCase()
        .includes(lowerCaseQuery) ||
      false;

    return (
      matchesSimpleFields || matchesDiagnosis || matchesTasks || matchesVisits || matchesDischarge
    );
  }

  private getSelectedList() {
    return this.caseLists.find(l => l.listIdentifier.selected);
  }

  private refresh(patients: VcPatientListItem[]): void {
    this.patients = patients || [];

    this.dataSource.data = this.applyQueryParamsFilter(patients);
  }

  private applyQueryParamsFilter(patients: VcPatientListItem[]): VcPatientListItem[] {
    const queryParams = this.activatedRoute.snapshot.queryParamMap;
    if (!queryParams) return patients;

    const filterType = ['department', 'service_unit', 'room'].find(e => queryParams.has(e));
    if (filterType) {
      const filterValue = queryParams.get(filterType);
      this.activeFilter = { type: filterType, value: filterValue ?? '' };
      switch (filterType) {
        case 'department':
          return patients.filter(e =>
            e.patient_details?.current_place?.data?.room?.service_unit?.department?.some(
              d => d.code === filterValue,
            ),
          );
        case 'service_unit':
          return patients.filter(
            e => e.patient_details?.current_place?.data?.room?.service_unit?.code === filterValue,
          );
        case 'room':
          return patients.filter(
            e => e.patient_details?.current_place?.data.room?.code === filterValue,
          );
      }
    }

    return patients;
  }

  private async updateData(): Promise<void> {
    if (!this.accessToken) return;

    try {
      const fetchedLists = (await this.vcCache.getCaseList()) as ExtendedPatientListGroup[];
      const currentStationName = this.activatedRoute.snapshot.paramMap.get('stationName');

      this.allSubs.push(
        this.translate.get('COMPONENT.PAGE_WORKFLOW.all_patients').subscribe((res: string) => {
          this.caseLists = [
            {
              listIdentifier: {
                id: -1,
                name: res,
                list_type: 'station',
                list_id: -1,
                selected: currentStationName ? false : true,
              },
              patients: fetchedLists.flatMap(group => group.patients),
            },
            ...fetchedLists.map(list => ({
              ...list,
              listIdentifier: {
                ...list.listIdentifier,
                selected: currentStationName === list.listIdentifier.name,
              },
            })),
          ];

          if (currentStationName) {
            const stationFromUrl = this.caseLists.find(
              station => station.listIdentifier.name === currentStationName,
            );

            if (stationFromUrl) {
              this.handleSelectedStation(stationFromUrl, false);
            } else {
              console.warn(
                `Station name "${currentStationName}" from URL not found in caseLists after loading`,
              );

              if (this.caseLists.length > 0) {
                this.handleSelectedStation(this.caseLists[0], true);
              }
            }
          }
        }),
      );
    } catch (error) {
      console.error('Error while refreshing data', error);
      this.refresh([]);
    }
  }

  getSelectedStationName(): string {
    const selectedStation = this.caseLists.find(list => list.listIdentifier.selected);
    return selectedStation ? selectedStation.listIdentifier.name : 'Organization';
  }

  public onPresentationModeChange(state: boolean): void {
    this.settingsFacade.settings$.pipe(take(1)).subscribe(s => {
      const newSettings: Settings = { ...s, presentationMode: state };
      this.settingsFacade.saveSettings(newSettings);
    });
  }

  public handleToggle(state: boolean): void {
    this.toggleState = state;
    this.onPresentationModeChange(state);

    this.clearViewedPatients();

    const selectedList = this.getSelectedList();
    if (selectedList) {
      this.refresh(selectedList.patients);
    }
  }

  public clearViewedPatients(): void {
    this.settingsFacade.settings$.pipe(take(1)).subscribe(s => {
      const newSettings: Settings = { ...s, viewedPatients: [] };
      this.settingsFacade.saveSettings(newSettings);
    });
  }

  public isPatientViewed(patient: VcPatientListItem): boolean {
    if (!patient?.patient_details?.case?.related_case_id) {
      return false;
    }

    const patientId = patient.patient_details.case.related_case_id;
    return Array.isArray(this.viewedPatients) && this.viewedPatients.includes(patientId);
  }

  public markPatientAsViewed(patient: VcPatientListItem): void {
    if (!patient?.patient_details?.case?.related_case_id) {
      return;
    }

    const patientId = patient.patient_details.case.related_case_id;

    if (!this.viewedPatients.includes(patientId)) {
      this.settingsFacade.settings$.pipe(take(1)).subscribe(s => {
        const updatedViewedPatients = [...(s.viewedPatients || []), patientId];
        const newSettings: Settings = { ...s, viewedPatients: updatedViewedPatients };
        this.settingsFacade.saveSettings(newSettings);
      });
    }
  }
  public sortByWard(patients: VcPatientListItem[], sortOrder: 'ASC' | 'DESC'): VcPatientListItem[] {
    if (!patients) return [];

    return patients.sort((a, b) => {
      const serviceUnitA = a.patient_details?.current_place?.data?.room?.service_unit?.code || '';
      const serviceUnitB = b.patient_details?.current_place?.data?.room?.service_unit?.code || '';

      let compare = serviceUnitA.localeCompare(serviceUnitB);

      if (compare === 0) {
        const roomA = a.patient_details?.current_place?.data?.code || '';
        const roomB = b.patient_details?.current_place?.data?.code || '';
        compare = roomA.localeCompare(roomB);
      }

      return sortOrder === 'ASC' ? compare : -compare;
    });
  }

  public getSelectedStationId(): string {
    const selectedStation = this.caseLists.find(list => list.listIdentifier.selected);
    return selectedStation ? selectedStation.listIdentifier.name : '';
  }

  public onStationSelectionChange(event: MatSelectChange): void {
    const selectedListName = event.value;
    const selectedList = this.caseLists.find(list => list.listIdentifier.name === selectedListName);

    if (selectedList) {
      this.handleSelectedStation(selectedList, true);
    }
  }
}
